import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Loader2, ChevronsUpDown, Check, X } from "lucide-react";
import { gsap } from "gsap";
import toast from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandGroup, CommandItem } from "./ui/command";
import { cn } from "@/lib/utils";
import { InputNew } from "./ui/inputNew";
import { TextareaNew } from "./ui/textareaNew";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const EstimateForm = () => {
  const submitBtnRef = useRef<HTMLButtonElement | null>(null);
  const textRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const text = textRef.current;

    if (!text) return;

    gsap.set([text], {
      opacity: 0,
      x: 50,
    });

    const tl = gsap.timeline({ delay: 0.5 });

    tl.to(text, {
      opacity: 1,
      x: 0,
      duration: 1,
      ease: "power3.out",
    });
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    services: [] as string[],
    budget: "",
    timeline: "",
    requirement: "",
  });

  const [loading, setLoading] = useState(false);

  const servicesList = [
    "Content Creation",
    "Digital Marketing",
    "Social Media Handling",
    "WhatsApp Bulk Messaging",
    "Tech Solution",
    "Graphic Designing",
    "Web & App Development",
    "SEO Optimization",
    "Media Solution",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    if (formData.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }

    setLoading(true);

    const url =
      "https://script.google.com/macros/s/AKfycbxl4xzRPtDAuwyb8fLm4ktJxbUI3q47AviiaqtKN536wxnnKzCjJAsRV5mk1vWNvx25/exec";

    try {
      const body = new URLSearchParams({
        Name: formData.name,
        Company: formData.company,
        Email: formData.email,
        Phone: formData.phone,
        Services: formData.services.join(", "),
        Budget: formData.budget,
        Timeline: formData.timeline,
        Requirement: formData.requirement,
      });

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const data = await res.text();

      if (res.ok) {
        toast.success("Estimate request submitted!");
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          services: [],
          budget: "",
          timeline: "",
          requirement: "",
        });
      } else {
        throw new Error(data || "Something went wrong");
      }
    } catch (error: any) {
      toast.error("Failed to submit. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }

    if (submitBtnRef.current) {
      gsap.to(submitBtnRef.current, {
        scale: 0.95,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: "power2.out",
      });
    }
  };

  return (
    <div
      ref={textRef}
      className="bg-transparent max-h-[87vh] md:max-h-[95vh] overflow-y-auto px-5 lg:px-14 w-full"
    >
      <h2 className="text-5xl text-[#0d1821] mb-6 md:mb-8 font-medium">
        Get Your
        <div className="pt-[6px]" />
        <span className="text-[#00a878]">Estimate</span>
      </h2>
      <p className="mb-3 text-md md:text-lg text-[#0d1821]/90 mx-auto font-poppins">
        Fill out the details and we’ll send you a tailored estimate.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5 md:space-y-8">
        <div className="grid md:grid-cols-2 gap-5 md:gap-8">
          <div className="form-field">
            <InputNew
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name *"
            />
          </div>

          <div className="form-field">
            <InputNew
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Company Name"
            />
          </div>

          <div className="form-field">
            <InputNew
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email *"
            />
          </div>

          <div className="form-field">
            <InputNew
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone *"
            />
          </div>
        </div>

        {/* Services */}
        <div className="form-field">
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center justify-between bg-[#f5f5f5] w-full text-md font-poppins text-[#0d1821]/70 pr-3 py-2 border border-t-0 border-l-0 border-r-0 rounded-none border-b-[#0d1821]/40 cursor-pointer min-h-[42px] ">
                <div className="flex flex-wrap gap-2">
                  {formData.services.length > 0 ? (
                    formData.services.map((service) => (
                      <span
                        key={service}
                        className="flex items-center gap-1 bg-[#00a878]/10 text-[#0d1821] px-2 py-1 rounded-full text-xs"
                      >
                        {service}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData((prev) => ({
                              ...prev,
                              services: prev.services.filter(
                                (s) => s !== service
                              ),
                            }));
                          }}
                        />
                      </span>
                    ))
                  ) : (
                    <span className="text-[#0d1821]/70">
                      Services Required *
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
              </div>
            </PopoverTrigger>

            <PopoverContent className="p-0 font-poppins w-[350px] sm:w-[450px] md:w-[550px] lg:w-[665px]">
              <Command className="w-full">
                <CommandGroup className="w-full">
                  {servicesList.map((service) => {
                    const isSelected = formData.services.includes(service);
                    return (
                      <CommandItem
                        key={service}
                        onSelect={() => {
                          setFormData((prev) => {
                            const exists = prev.services.includes(service);
                            return {
                              ...prev,
                              services: exists
                                ? prev.services.filter((s) => s !== service)
                                : [...prev.services, service],
                            };
                          });
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 ",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {service}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Budget */}
        <div className="form-field">
          <Select
            value={formData.budget}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, budget: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Budget range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Under ₹10k">Under ₹10k</SelectItem>
              <SelectItem value="₹10k–25k">₹10k–25k</SelectItem>
              <SelectItem value="₹25k–50k">₹25k–50k</SelectItem>
              <SelectItem value="₹50k+">₹50k+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Timeline */}
        <div className="form-field">
          <Select
            value={formData.timeline}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, timeline: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Project timeline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Urgent">Urgent</SelectItem>
              <SelectItem value="1 Month">1 Month</SelectItem>
              <SelectItem value="3 Months">3 Months</SelectItem>
              <SelectItem value="Flexible">Flexible</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requirement */}
        <div className="form-field">
          <TextareaNew
            id="requirement"
            name="requirement"
            rows={window.innerWidth < 500 ? 1 : window.innerWidth < 700 ? 2 : 3}
            value={formData.requirement}
            onChange={handleInputChange}
            placeholder="Message"
          />
        </div>

        <Button
          ref={submitBtnRef}
          type="submit"
          disabled={loading}
          className="submit-btn btn-hero w-full pt-3 text-lg font-medium group text-[#0d1821] disabled:opacity-70"
        >
          {loading ? (
            <Loader2 className="mr-2 w-5 h-5 animate-spin text-[#0d1821]" />
          ) : (
            <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform text-[#0d1821]" />
          )}
          {loading ? "Submitting..." : "Get Estimate"}
        </Button>
      </form>
    </div>
  );
};

export default EstimateForm;
